// Centralized dayjs wrapper for the app.
// Use this file as the single source of configuration (plugins, timezone, locales).
// Import from '@/lib/dayjs' in other modules, e.g.:
//   import { dayjs, parseIsoToTz } from '@/lib/dayjs';
// Do NOT import 'dayjs' or its plugins directly elsewhere.
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import 'dayjs/locale/es';

// Configure dayjs with UTC and timezone plugins and set default app timezone
dayjs.extend(utc);
dayjs.extend(timezone);

export const APP_TZ = 'Europe/Madrid';
// Format for persisted datetimes: 'YYYY-MM-DDTHH:mm' (no seconds, no TZ suffix)
const APP_ISO_FORMAT = 'YYYY-MM-DDTHH:mm';

// set default tz for any tz-aware formatting
// @ts-ignore - dayjs.tz may be present via plugin
dayjs.tz.setDefault(APP_TZ);

// parse an ISO datetime string into a dayjs in APP_TZ
// Handles both local ISO (no timezone suffix) and UTC ISO (with Z or offset)
export function parseIsoToTz(iso: string) {
  // Check if string has timezone info (Z, +HH:MM, -HH:MM)
  // Use a more precise check to avoid false positives with date parts
  const hasTimezone = iso.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(iso);

  if (hasTimezone) {
    // Has timezone info, parse as UTC then convert to APP_TZ
    return dayjs(iso).tz(APP_TZ);
  }
  // No timezone suffix, interpret as already being in APP_TZ
  // Use format string to tell dayjs.tz to parse as local time in target TZ
  // Supports both 'YYYY-MM-DDTHH:mm' and 'YYYY-MM-DDTHH:mm:ss' formats
  const format = iso.length > 16 ? 'YYYY-MM-DDTHH:mm:ss' : 'YYYY-MM-DDTHH:mm';
  return dayjs.tz(iso, format, APP_TZ);
}

// return difference in hours (can be fractional) between two ISO datetimes interpreted in APP_TZ
export function diffHours(inicioIso: string, finIso: string) {
  const inicio = parseIsoToTz(inicioIso);
  const fin = parseIsoToTz(finIso);
  if (!inicio.isValid() || !fin.isValid()) {
    throw new Error('Invalid ISO datetime');
  }
  if (fin.isBefore(inicio) || fin.isSame(inicio)) {
    throw new Error('`fin` must be after `inicio`');
  }
  const minutes = fin.diff(inicio, 'minute');
  return minutes / 60;
}

export { dayjs };

// Converts a dayjs instance to app-local ISO string in APP_TZ
export function toAppIso(d: dayjs.Dayjs): string {
  // Keep local wall-clock time when the instance has no explicit timezone to avoid double-shifting
  const hasTimezone = Boolean((d as unknown as { $x?: { $timezone?: string } }).$x?.$timezone);
  const target = d.isUTC() || hasTimezone ? d.tz(APP_TZ) : d.tz(APP_TZ, true);
  return target.format(APP_ISO_FORMAT);
}

// Converts a native Date to app-local ISO string
export function dateToAppIso(date: Date): string {
  return toAppIso(dayjs(date));
}

// Returns current moment as app-local ISO string
export function nowAppIso(): string {
  return toAppIso(dayjs());
}

// Converts an ISO string to Date in APP_TZ for UI pickers
export function isoToDateInAppTz(iso?: string | null): Date | null {
  if (!iso) {
    return null;
  }
  try {
    const d = parseIsoToTz(iso);
    return d.isValid() ? d.toDate() : null;
  } catch (e) {
    return null;
  }
}
